import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Plus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { ErrorNotice, Loading, Empty } from "../../components/Feedback";
import { Modal } from "../../components/Modal";
import { useSession } from "../../state/SessionContext";
import { contractApi } from "./api";
import type { ContractCatalog } from "./api";
import { ContractUsersPanel } from "./ContractUsersPanel";
import "./contract-admin.css";

type Tab = "areas" | "frentes" | "usuarios";
type Editor = { kind: "area" | "frente" | "subfrente"; id?: string } | null;
const blank: ContractCatalog = { admin: false, coordinador_areas: [], areas: [], predefinidas: [], frentes: [], subfrentes: [], usuarios: [] };

export function ContractAdminPage() {
  const { context, refresh, session } = useSession();
  const [tab,setTab] = useState<Tab>("areas");
  const [data,setData] = useState<ContractCatalog>(blank);
  const [loading,setLoading] = useState(true);
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState("");
  const [notice,setNotice] = useState("");
  const [edit,setEdit] = useState<Editor>(null);
  const [name,setName] = useState("");
  const [description,setDescription] = useState("");
  const [frontId,setFrontId] = useState("");
  const [selectedFrontId,setSelectedFrontId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!context) return;
    setError("");
    try { setData(await contractApi<ContractCatalog>(context,"catalogos")); }
    catch(caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setLoading(false); }
  }, [context]);
  useEffect(()=>{setEdit(null);void reload();},[reload]);
  useEffect(()=>{
    if (!error) return;
    const timeout=window.setTimeout(()=>setError(""),4000);
    return ()=>window.clearTimeout(timeout);
  },[error]);
  useEffect(()=>{
    if (!notice) return;
    const timeout=window.setTimeout(()=>setNotice(""),3000);
    return ()=>window.clearTimeout(timeout);
  },[notice]);
  const execute = async (operation:string,payload:Record<string,unknown>,message:string) => {
    if (!context || busy) return;
    setBusy(true);setError("");
    try {
      await contractApi(context,operation,payload);
      setEdit(null);
      setNotice(message);
      await reload();
      await refresh();
    } catch(caught) {setError(caught instanceof Error ? caught.message : String(caught));}
    finally {setBusy(false);}
  };
  const confirmRemove = (message:string,operation:string,id:string) => {
    if (window.confirm(message)) void execute(operation,{id},"Registro retirado.");
  };
  const start = (kind:NonNullable<Editor>["kind"],id?:string) => {
    const area=data.areas.find(a=>a.id===id);
    const front=data.frentes.find(f=>f.id===id);
    const sub=data.subfrentes.find(s=>s.id===id);
    setName(area?.area ?? front?.frente ?? sub?.subfrente ?? "");
    setDescription(area?.descripcion ?? "");
    setFrontId(sub?.frente_id ?? data.frentes[0]?.id ?? "");
    setError("");
    setEdit({kind,id});
  };
  const selectedFront = data.frentes.find(f => f.id === selectedFrontId) ?? data.frentes[0];
  const selectedSubfronts = selectedFront
    ? data.subfrentes.filter(s => s.frente_id === selectedFront.id) : [];
  const areaCounts = new Map(data.areas.map(area => [
    area.id,
    new Set(data.usuarios.filter(u => u.asignaciones.some(a =>
      a.area_id === area.id && a.is_active)).map(u => u.user_id)).size,
  ]));
  const submit=(event:FormEvent) => {
    event.preventDefault();
    if (!edit || !data.admin) return;
    if (edit.kind==="area") void execute("crear_area",{nombre:name.trim(),descripcion:description.trim()},"Área registrada.");
    if (edit.kind==="frente") void execute(edit.id?"editar_frente":"crear_frente",{id:edit.id,nombre:name.trim()},"Frente guardado.");
    if (edit.kind==="subfrente") void execute(edit.id?"editar_subfrente":"crear_subfrente",{
      id:edit.id,nombre:name.trim(),frente_id:frontId,
    },"Subfrente guardado.");
  };
  return <div className="ca-page">
    <div className="page-heading"><div>
      <span className="eyebrow">CONFIGURACIÓN DEL CONTRATO</span>
      <h1>Administración del contrato</h1>
      <p>{context?.contrato_numero} · Áreas operativas, frentes y usuarios</p>
    </div><button className="button secondary" onClick={()=>void reload()}><RefreshCw size={17}/>Actualizar</button></div>
    <div className="tabs">{([["areas","Áreas operativas"],["frentes","Frentes y subfrentes"],["usuarios","Usuarios"]] as const).map(([key,title])=>
      <button key={key} className={tab===key?"active":""} onClick={()=>{setTab(key);setError("");}}>{title}</button>)}</div>
    {error && <ErrorNotice message={error}/>}
    {notice && <div className="notice success">{notice}</div>}
    {loading ? <Loading/> : <>
      {tab === "areas" && <section className="panel">
        <div className="toolbar ca-areas-toolbar"><strong>Áreas del contrato ({data.areas.length})</strong>{data.admin && <div className="ca-actions ca-area-tools"><select aria-label="Agregar área predefinida" defaultValue="" onChange={e => { const id=e.target.value; e.target.value=""; if(id) void execute("crear_area",{default_area_id:id},"Área predefinida agregada."); }}><option value="">Agregar área predefinida…</option>{data.predefinidas.filter(a => !data.areas.some(b=>b.default_area_id===a.id)).map(a=><option key={a.id} value={a.id}>{a.nombre}</option>)}</select><button className="button primary" onClick={()=>start("area")}><Plus size={16}/>Nueva área</button></div>}</div>
        {data.areas.length ? <div className="table-wrap"><table><thead><tr><th>ÁREA</th><th>DESCRIPCIÓN</th><th>USUARIOS</th><th>ORIGEN</th>{data.admin && <th>ACCIONES</th>}</tr></thead><tbody>{data.areas.map(a=><tr key={a.id}><td><strong>{a.area}</strong></td><td>{a.descripcion || "—"}</td><td>{areaCounts.get(a.id) ?? 0}</td><td>{a.default_area_id?"Predefinida":"Personalizada"}</td>{data.admin && <td><button className="icon-button danger" aria-label={"Quitar "+a.area} title="Quitar área" onClick={()=>confirmRemove("¿Quitar el área? Solo se permite cuando no tiene usuarios activos.","quitar_area",a.id)}><Trash2 size={17}/></button></td>}</tr>)}</tbody></table></div> : <Empty message="Sin áreas registradas."/>}
      </section>}
      {tab === "frentes" && <section className="panel">
        <div className="toolbar ca-front-toolbar">
          <strong>Frentes y subfrentes</strong>
          {data.admin && <button className="button primary" onClick={()=>start("frente")}><Plus size={16}/>Nuevo frente</button>}
        </div>
        <div className="ca-front-stats" aria-label="Totales del contrato">
          <div><span>Frentes del contrato</span><strong>{data.frentes.length}</strong></div>
          <div><span>Subfrentes del contrato</span><strong>{data.subfrentes.length}</strong></div>
        </div>
        {!data.frentes.length ? <Empty message="No hay frentes registrados."/> :
          <div className="ca-front-workspace">
            <section className="ca-front-pane" aria-label="Listado de frentes">
              <div className="ca-pane-title"><strong>Frentes</strong><span>{data.frentes.length}</span></div>
              <div className="ca-front-options">
                {data.frentes.map(f=>{
                  const count=data.subfrentes.filter(s=>s.frente_id===f.id).length;
                  const active=selectedFront?.id===f.id;
                  return <div className={`ca-front-option${active?" selected":""}`} key={f.id}>
                    <button type="button" className="ca-front-select" aria-current={active?"true":undefined}
                      onClick={()=>setSelectedFrontId(f.id)}>
                      <span>{f.frente}</span><small>{count} {count===1?"subfrente":"subfrentes"}</small>
                    </button>
                    {data.admin && <div className="ca-actions">
                      <button className="icon-button" title={`Editar ${f.frente}`} aria-label={`Editar ${f.frente}`} onClick={()=>start("frente",f.id)}><Pencil size={16}/></button>
                      <button className="icon-button danger" title={`Eliminar ${f.frente}`} aria-label={`Eliminar ${f.frente}`}
                        onClick={()=>confirmRemove("¿Quitar este frente y todos sus subfrentes?","quitar_frente",f.id)}><Trash2 size={16}/></button>
                    </div>}
                  </div>;
                })}
              </div>
            </section>
            <section className="ca-subfront-pane" aria-label="Subfrentes del frente seleccionado">
              <div className="ca-pane-title ca-subfront-heading">
                <div><small>FRENTE SELECCIONADO</small><strong>{selectedFront?.frente}</strong>
                  <span>{selectedSubfronts.length} {selectedSubfronts.length===1?"subfrente":"subfrentes"}</span></div>
                {data.admin && selectedFront && <button className="button primary small" onClick={()=>{start("subfrente");setFrontId(selectedFront.id);}}><Plus size={16}/>Nuevo subfrente</button>}
              </div>
              {selectedSubfronts.length ? <div className="ca-subfront-rows">
                {selectedSubfronts.map(s=><div className="ca-subfront-row" key={s.id}>
                  <span>{s.subfrente}</span>
                  {data.admin && <div className="ca-actions">
                    <button className="icon-button" title={`Editar ${s.subfrente}`} aria-label={`Editar ${s.subfrente}`} onClick={()=>start("subfrente",s.id)}><Pencil size={16}/></button>
                    <button className="icon-button danger" title={`Eliminar ${s.subfrente}`} aria-label={`Eliminar ${s.subfrente}`}
                      onClick={()=>confirmRemove("¿Quitar este subfrente?","quitar_subfrente",s.id)}><Trash2 size={16}/></button>
                  </div>}
                </div>)}
              </div> : <div className="ca-no-subfronts">Este frente no tiene subfrentes registrados.</div>}
            </section>
          </div>}
      </section>}

      {tab==="usuarios" && context && <ContractUsersPanel catalog={data} context={context}
        reload={reload} refresh={refresh} currentUserId={session?.user.id} />}
    </>}
    {edit && data.admin && <Modal title={edit.kind==="area"?"Registrar área":edit.kind==="frente"?"Gestionar frente":"Gestionar subfrente"}
      onClose={()=>{if(!busy)setEdit(null);}} className="ca-modal">
      <form className="ca-form" onSubmit={submit}>
        <label>Nombre<input required value={name} onChange={e=>setName(e.target.value)}/></label>
        {edit.kind==="area" && <label>Descripción<textarea value={description} onChange={e=>setDescription(e.target.value)}/></label>}
        {edit.kind==="subfrente" && !edit.id && <label>Frente<select required value={frontId}
          onChange={e=>setFrontId(e.target.value)}>
          {data.frentes.map(f=><option key={f.id} value={f.id}>{f.frente}</option>)}
        </select></label>}
        {error && <ErrorNotice message={error}/>}
        <div className="ca-form-footer">
          <button type="button" className="button secondary" onClick={()=>setEdit(null)}>Cancelar</button>
          <button type="submit" className="button primary" disabled={busy}>{busy?"Guardando…":"Guardar"}</button>
        </div>
      </form>
    </Modal>}
  </div>;
}
